<script setup>
import { computed, ref, watch, nextTick } from "vue";
import { useStore } from "vuex";
import { useI18n } from "vue-i18n";
import { intlLocale } from "kanban/constants";
import { useAlert } from "dashboard/composables";
import Button from "dashboard/components-next/button/Button.vue";
import Input from "dashboard/components-next/input/Input.vue";
import MoneyInput from "kanban/components/MoneyInput.vue";
import Dialog from "dashboard/components-next/dialog/Dialog.vue";
import { parseAPIErrorResponse } from "dashboard/store/utils/api";
import kanbanModule from "kanban/store/modules/kanban";

const props = defineProps({
	open: {
		type: Boolean,
		default: false,
	},
	boardId: {
		type: Number,
		required: true,
	},
	currency: {
		type: String,
		default: "USD",
	},
	initialName: {
		type: String,
		default: "",
	},
	product: {
		type: Object,
		default: null,
	},
});

const emit = defineEmits(["created", "updated", "close"]);

const store = useStore();
const { t, locale } = useI18n();

if (!store.hasModule("kanban")) {
	store.registerModule("kanban", kanbanModule);
}

const productModalRef = ref(null);
const discardDialogRef = ref(null);
const showDiscardDialog = ref(false);
const isSaving = ref(false);
const skipCloseGuard = ref(false);

const productName = ref("");
const productDescription = ref("");
const productUnitPrice = ref("");

const isEditing = computed(() => Boolean(props.product));

const currencySymbol = computed(() => {
	return (
		new Intl.NumberFormat(intlLocale(locale.value), {
			style: "currency",
			currency: props.currency,
		})
			.formatToParts(0)
			.find((p) => p.type === "currency")?.value || props.currency
	);
});

const toMoneyNumber = (val) => {
	if (val === null || val === undefined || val === "") return null;
	const num = typeof val === "number" ? val : parseFloat(val);
	if (Number.isNaN(num)) return null;
	return Number(num.toFixed(2));
};

const resetForm = () => {
	productName.value = "";
	productDescription.value = "";
	productUnitPrice.value = "";
};

const hydrateForm = () => {
	if (props.product) {
		productName.value = props.product.name || "";
		productDescription.value = props.product.description || "";
		productUnitPrice.value = toMoneyNumber(props.product.unit_price) ?? "";
		return;
	}
	productName.value = props.initialName || "";
	productDescription.value = "";
	productUnitPrice.value = "";
};

const hasModalChanges = computed(() => {
	if (props.product) {
		return (
			productName.value !== props.product.name ||
			productDescription.value !== (props.product.description || "") ||
			toMoneyNumber(productUnitPrice.value) !==
				toMoneyNumber(props.product.unit_price)
		);
	}
	const initialName = props.initialName || "";
	return (
		productName.value !== initialName ||
		productDescription.value !== "" ||
		productUnitPrice.value !== ""
	);
});

const dismiss = () => {
	skipCloseGuard.value = true;
	resetForm();
	showDiscardDialog.value = false;
	emit("close");
};

const handleClose = async () => {
	if (isSaving.value || showDiscardDialog.value || skipCloseGuard.value) return;
	if (hasModalChanges.value) {
		showDiscardDialog.value = true;
		await nextTick();
		productModalRef.value?.open();
		return;
	}
	dismiss();
};

const confirmDiscard = () => {
	showDiscardDialog.value = false;
	dismiss();
};

const closeDiscardDialog = () => {
	showDiscardDialog.value = false;
};

const saveProduct = async () => {
	if (!productName.value.trim()) return;

	isSaving.value = true;
	try {
		const productData = {
			name: productName.value.trim(),
			description: productDescription.value.trim(),
			unit_price: toMoneyNumber(productUnitPrice.value) ?? 0,
		};

		if (props.product) {
			const updated = await store.dispatch("kanban/updateProduct", {
				boardId: props.boardId,
				productId: props.product.id,
				productData,
			});
			useAlert(t("KANBAN.PRODUCTS.UPDATE_SUCCESS"));
			emit("updated", updated);
		} else {
			const created = await store.dispatch("kanban/createProduct", {
				boardId: props.boardId,
				productData,
			});
			useAlert(t("KANBAN.PRODUCTS.CREATE_SUCCESS"));
			emit("created", created);
		}
		dismiss();
	} catch (error) {
		useAlert(parseAPIErrorResponse(error) || t("KANBAN.PRODUCTS.SAVE_ERROR"));
	} finally {
		isSaving.value = false;
	}
};

watch(
	() => props.open,
	async (val) => {
		if (val) {
			skipCloseGuard.value = false;
			hydrateForm();
			await nextTick();
			productModalRef.value?.open();
		} else {
			showDiscardDialog.value = false;
		}
	},
);

watch(showDiscardDialog, async (val) => {
	if (val) {
		await nextTick();
		discardDialogRef.value?.open();
	}
});
</script>

<template>
  <Dialog
    v-if="open"
    ref="productModalRef"
    :title="
      isEditing
        ? t('KANBAN.PRODUCTS.EDIT_PRODUCT')
        : t('KANBAN.PRODUCTS.ADD_PRODUCT')
    "
    @close="handleClose"
    @confirm="saveProduct"
  >
    <div class="flex flex-col gap-4">
      <label class="flex flex-col gap-2 text-sm font-medium text-n-slate-12">
        {{ t('KANBAN.PRODUCTS.FORM.NAME_LABEL') }}
        <Input
          v-model="productName"
          :placeholder="t('KANBAN.PRODUCTS.FORM.NAME_PLACEHOLDER')"
          maxlength="255"
          autocomplete="off"
        />
      </label>
      <label class="flex flex-col gap-2 text-sm font-medium text-n-slate-12">
        {{ t('KANBAN.PRODUCTS.FORM.DESCRIPTION_LABEL') }}
        <Input
          v-model="productDescription"
          :placeholder="t('KANBAN.PRODUCTS.FORM.DESCRIPTION_PLACEHOLDER')"
          maxlength="500"
          autocomplete="off"
        />
      </label>
      <label class="flex flex-col gap-2 text-sm font-medium text-n-slate-12">
        {{
          t('KANBAN.PRODUCTS.FORM.UNIT_PRICE_LABEL_WITH_CURRENCY', {
            symbol: currencySymbol,
          })
        }}
        <MoneyInput
          v-model="productUnitPrice"
          :placeholder="t('KANBAN.PRODUCTS.FORM.UNIT_PRICE_PLACEHOLDER')"
          :min="0"
        />
      </label>
    </div>
    <template #footer>
      <div class="flex justify-end gap-2">
        <Button type="button" variant="ghost" @click="handleClose">
          {{ t('KANBAN.PRODUCTS.FORM.CANCEL') }}
        </Button>
        <Button
          type="button"
          :disabled="!productName.trim() || isSaving"
          :is-loading="isSaving"
          @click="saveProduct"
        >
          {{
            isEditing
              ? t('KANBAN.PRODUCTS.FORM.UPDATE')
              : t('KANBAN.PRODUCTS.FORM.CREATE')
          }}
        </Button>
      </div>
    </template>
  </Dialog>

  <Dialog
    v-if="showDiscardDialog"
    ref="discardDialogRef"
    type="alert"
    :title="t('KANBAN.PRODUCTS.DISCARD_TITLE')"
    :description="t('KANBAN.PRODUCTS.DISCARD_CONFIRMATION')"
    @close="closeDiscardDialog"
  >
    <template #footer>
      <div class="flex items-center justify-between w-full gap-3">
        <Button
          variant="faded"
          color="slate"
          :label="t('KANBAN.PRODUCTS.FORM.CANCEL')"
          class="w-full"
          @click="closeDiscardDialog"
        />
        <Button
          color="ruby"
          :label="t('KANBAN.PRODUCTS.DISCARD')"
          class="w-full"
          @click="confirmDiscard"
        />
      </div>
    </template>
  </Dialog>
</template>
